import React, { useState, useEffect } from 'react'
import HomeTemplate from '../templates/HomeTemplate'
import { HelpCircle, Search, ChevronDown, ChevronUp, Cake, FileText, Image, RefreshCw, Lock, Percent, Video, Volume2, QrCode, Code, FileDown, Link } from 'lucide-react'
import useCanonicalUrl from '../hooks/useCanonicalUrl'
import './FAQPage.css'

const FAQPage = () => {
  useCanonicalUrl();
  const [activeCategory, setActiveCategory] = useState('all')
  const [expandedItems, setExpandedItems] = useState(new Set(['general-0']))
  const [searchTerm, setSearchTerm] = useState('')

  const faqData = [
    {
      id: 'general',
      name: 'General Questions',
      icon: HelpCircle,
      color: '#00f5a0',
      questions: [
        {
          question: "Are EveryKits tools really free to use?",
          answer: "Yes, absolutely! All our tools are completely free with no hidden charges, premium features, or subscription requirements. We believe in democratizing access to essential digital utilities for everyone."
        },
        {
          question: "How do you keep the tools free?",
          answer: "We're funded through minimal, non-intrusive advertising and partnerships with companies who share our vision. This allows us to maintain and improve our tools without charging users."
        },
        {
          question: "Is my data safe and private?",
          answer: "Yes, absolutely! Your privacy is our top priority. All our tools process data entirely in your browser using client-side JavaScript. We never store your files, track your inputs, or collect personal information."
        },
        {
          question: "Do I need to create an account?",
          answer: "No account needed! You can use all our tools instantly without registration, email addresses, or sign-up processes. Just visit the tool and start using it immediately."
        },
        {
          question: "Can I use these tools on mobile devices?",
          answer: "Yes! All our tools are fully responsive and work perfectly on smartphones, tablets, and desktop computers. The interface automatically adapts to your screen size."
        },
        {
          question: "What browsers are supported?",
          answer: "Our tools work best on modern browsers including Chrome, Firefox, Safari, and Edge. We recommend keeping your browser updated for the best performance."
        },
        {
          question: "Can I suggest new tools or features?",
          answer: "We love feedback! You can reach out through our contact page with suggestions for new tools or improvements. We're always looking to expand our utility belt."
        }
      ]
    },
    {
      id: 'age',
      name: 'Age Calculator',
      icon: Cake,
      color: '#ff6b6b',
      questions: [
        {
          question: "How accurate is the age calculator?",
          answer: "Our age calculator is extremely accurate, calculating your exact age in years, months, days, hours, and minutes. It accounts for leap years and varying month lengths."
        },
        {
          question: "What date format should I use?",
          answer: "You can use any standard date format including MM/DD/YYYY, DD/MM/YYYY, or YYYY-MM-DD. Our calculator automatically recognizes different date formats."
        },
        {
          question: "Does it account for leap years?",
          answer: "Yes, our age calculator automatically accounts for leap years in its calculations. This ensures accurate age calculations for people born on February 29th."
        },
        {
          question: "Can I calculate age for historical dates?",
          answer: "Absolutely! Our calculator works with any date in the past or future. You can calculate ages for historical figures or future dates with the same accuracy."
        },
        {
          question: "What is zodiac sign calculation?",
          answer: "Our calculator determines your zodiac sign based on your birth date according to Western astrology. This adds a fun element to the age calculation."
        }
      ]
    },
    {
      id: 'word-counter',
      name: 'Word Counter',
      icon: FileText,
      color: '#4ecdc4',
      questions: [
        {
          question: "What types of text can I analyze?",
          answer: "You can analyze any text content including essays, articles, blog posts, academic papers, social media content, and more. Our word counter works with English and other languages."
        },
        {
          question: "Does it count characters with or without spaces?",
          answer: "Our word counter provides both character counts - with spaces and without spaces. This helps you meet different requirements for platforms with character limits."
        },
        {
          question: "How does keyword density analysis work?",
          answer: "Keyword density analysis identifies the most frequently used words in your text and shows their frequency. This helps SEO professionals optimize content."
        },
        {
          question: "Can it handle large documents?",
          answer: "Yes, our word counter can handle substantial text content. For extremely large documents, we recommend breaking them into smaller chunks for better performance."
        },
        {
          question: "How accurate are the reading time estimates?",
          answer: "Reading time estimates are based on the average adult reading speed of 200-250 words per minute. Speaking time estimates use average speech rates."
        }
      ]
    },
    {
      id: 'image-compressor',
      name: 'Image Compressor',
      icon: Image,
      color: '#45b7d1',
      questions: [
        {
          question: "How much can I reduce image file size?",
          answer: "Compression results vary by image type and quality settings, but most users achieve 50-80% file size reduction while maintaining excellent visual quality."
        },
        {
          question: "What image formats are supported?",
          answer: "Our image compressor supports JPG, JPEG, PNG, WebP, GIF, and BMP formats. You can upload images in any of these formats."
        },
        {
          question: "Will compression affect image quality?",
          answer: "Our compression algorithm is designed to minimize quality loss. At recommended quality settings (80-90%), most users cannot detect any visual difference."
        },
        {
          question: "Are my images uploaded to your servers?",
          answer: "No, all image processing happens entirely in your browser using client-side JavaScript. Your images never leave your device, ensuring complete privacy."
        },
        {
          question: "What quality settings should I use?",
          answer: "For web use, 80-90% quality provides excellent balance between file size and visual quality. For print use, use 90-95% quality."
        }
      ]
    },
    {
      id: 'password',
      name: 'Password Generator',
      icon: Lock,
      color: '#5f27cd',
      questions: [
        {
          question: "How secure are the generated passwords?",
          answer: "Our password generator uses cryptographically secure random number generation to create truly random passwords. Each password is unpredictable and secure."
        },
        {
          question: "What password options can I customize?",
          answer: "You can customize password length (8-128 characters), include or exclude uppercase letters, lowercase letters, numbers, and special characters."
        },
        {
          question: "Are passwords stored or saved anywhere?",
          answer: "No, passwords are never stored, saved, or transmitted anywhere. They are generated entirely in your browser and disappear when you close the page."
        },
        {
          question: "What makes a strong password?",
          answer: "A strong password should be at least 12 characters long, include uppercase and lowercase letters, numbers, and special characters. Avoid personal information."
        },
        {
          question: "Should I use password managers?",
          answer: "Absolutely! Password managers are the best way to use strong, unique passwords for every account. Generate passwords with our tool and store them securely."
        }
      ]
    },
    {
      id: 'percentage',
      name: 'Percentage Calculator',
      icon: Percent,
      color: '#00d2d3',
      questions: [
        {
          question: "What percentage calculations can I do?",
          answer: "Our percentage calculator handles various calculations including: X is what % of Y?, What is X% of Y?, Percentage increase/decrease, and percentage change."
        },
        {
          question: "How do I calculate percentage increase?",
          answer: "For percentage increase, use the 'Percentage Change' calculation. Enter the original value and the new value to see how much it increased as a percentage."
        },
        {
          question: "Can it calculate reverse percentages?",
          answer: "Yes! The 'X is what % of Y?' calculation is essentially a reverse percentage calculation. This helps you find what percentage one number is of another."
        },
        {
          question: "How accurate are the calculations?",
          answer: "Our calculator provides precise results with multiple decimal places for accuracy. For most practical purposes, you can round to 2 decimal places."
        },
        {
          question: "Can I use it for financial calculations?",
          answer: "Yes, our percentage calculator is perfect for financial calculations including interest rates, profit margins, discounts, and tax rates."
        }
      ]
    }
  ]

  useEffect(() => {
    document.title = 'Frequently Asked Questions - EveryKits'

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Find answers to common questions about EveryKits free online tools, including how to use each calculator, converter, and generator.')
    }

    // Add FAQ structured data for Google rich results
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.flatMap(category =>
        category.questions.map((faq, index) => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      )
    }

    // Add structured data to head
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(structuredData)
    document.head.appendChild(script)

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  const toggleExpanded = (itemId) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId)
    } else {
      newExpanded.add(itemId)
    }
    setExpandedItems(newExpanded)
  }

  const toggleCategory = (category) => {
    setActiveCategory(category === activeCategory ? 'all' : category)
  }

  const filteredFAQs = faqData.map(category => ({
    ...category,
    questions: category.questions.filter(faq => {
      const matchesCategory = activeCategory === 'all' || activeCategory === category.id
      const matchesSearch = searchTerm === '' ||
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
      return matchesCategory && matchesSearch
    })
  })).filter(category => category.questions.length > 0)

  const totalQuestions = faqData.reduce((total, category) => total + category.questions.length, 0)

  return (
    <HomeTemplate>
      <div className="faq-page">
        {/* Hero Section */}
        <section className="faq-hero">
          <h1>Frequently Asked Questions</h1>
          <p className="hero-description">
            Find answers to common questions about EveryKits tools and learn how to make the most of our free online utilities.
          </p>

          {/* Search Bar */}
          <div className="search-container">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </section>

        {/* Category Filter */}
        <section className="faq-categories">
          <h2>Browse by Category</h2>
          <div className="category-grid">
            <button
              className={`category-btn ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => setActiveCategory('all')}
            >
              <HelpCircle className="category-icon" />
              <span>All Questions</span>
            </button>
            {faqData.map((category) => (
              <button
                key={category.id}
                className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => toggleCategory(category.id)}
              >
                <category.icon className="category-icon" style={{ color: category.color }} />
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </section>

        {/* FAQ Items */}
        <section className="faq-content">
          {filteredFAQs.length > 0 ? (
            filteredFAQs.map((category) => (
              <div key={category.id} className="faq-category">
                <h3 className="category-title">
                  <category.icon className="category-icon" style={{ color: category.color }} />
                  {category.name}
                </h3>
                <div className="faq-list">
                  {category.questions.map((faq, index) => {
                    const itemId = `${category.id}-${index}`
                    const isExpanded = expandedItems.has(itemId)

                    return (
                      <div key={itemId} className="faq-item">
                        <button
                          className={`faq-question ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleExpanded(itemId)}
                        >
                          <span>{faq.question}</span>
                          {isExpanded ? <ChevronUp /> : <ChevronDown />}
                        </button>
                        {isExpanded && (
                          <div className="faq-answer">
                            <p>{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="no-results">
              <HelpCircle className="no-results-icon" />
              <h3>No questions found</h3>
              <p>Try adjusting your search terms or browse all categories.</p>
            </div>
          )}
        </section>

        {/* Stats Section */}
        <section className="faq-stats">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">{totalQuestions}</div>
              <div className="stat-label">Total Questions</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{faqData.length}</div>
              <div className="stat-label">Tool Categories</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Free Tools</div>
            </div>
          </div>
        </section>

        {/* Help Section */}

      </div>
    </HomeTemplate>
  )
}

export default FAQPage